import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw, Percent, CalendarCheck } from "lucide-react";
import PhotoSummaryCard from "./PhotoSummaryCard";
import RowCard from "./RowCard";

// Intra-scan dedup key: due righe con stesso nome+categoria (normalizzati)
// puntano allo stesso prodotto — la seconda si aggancerà alla prima al Save.
function _intraScanKey(name, category) {
  const n = (name || "").toLowerCase().normalize("NFKD").replace(/\s+/g, " ").trim();
  const c = (category || "").toLowerCase().trim();
  if (!n || !c) return null;
  return `${n}|${c}`;
}

export default function ReviewPhase({
  photos,
  rows,
  isProduct,
  products,
  selectedCount,
  saving,
  onUpdateRow,
  onRemoveRow,
  onApplyGlobalDiscount,
  onApplySuggestedExpiryToAll,
  onReset,
  onCancel,
  onSave,
  onResolveSupplier,
}) {
  const successPhotos = photos.filter((p) => p.status === "done");
  const [globalDiscount, setGlobalDiscount] = useState("");

  const applyGlobalDiscount = () => {
    const v = Math.max(0, Math.min(100, Number(globalDiscount) || 0));
    onApplyGlobalDiscount?.(v);
  };

  // Quante righe prodotto senza scadenza ma con suggested AI disponibile?
  const missingExpiryCount = isProduct
    ? rows.filter((r) => !r.expiry_date && r.suggested_expiry_date).length
    : 0;

  // Mappa intra-scan: rowId → indice+riga della prima riga con stessa key.
  // Regole:
  //  - Riga con __match_rejected NON viene agganciata (non compare come chiave nella mappa),
  //    ma può comunque essere "target" per righe successive (è comunque una card).
  //  - Se una riga ha già un __link_to_id esplicito, non ha bisogno di essere agganciata
  //    intra-scan: si aggancerà a quel prodotto direttamente.
  const intraScanLinks = useMemo(() => {
    if (!isProduct) return new Map();
    const map = new Map();
    const seen = new Map(); // key → { index, row }
    rows.forEach((r, idx) => {
      const key = _intraScanKey(r.name, r.category);
      if (!key) return;
      if (!seen.has(key)) {
        seen.set(key, { index: idx, row: r });
        return;
      }
      // Riga successiva con stessa key: si aggancerà alla prima — salvo rifiuto esplicito
      if (r.__match_rejected) return;
      // Se la riga ha già un link diverso da quello della prima, rispettiamo il suo link
      // (potrebbe puntare a un altro prodotto già in magazzino) e non annotiamo l'aggancio intra-scan.
      const first = seen.get(key);
      if (r.__link_to_id && r.__link_to_id !== first.row.__link_to_id) return;
      map.set(r.__id, { targetIndex: first.index, targetBatch: r.batch || "" });
    });
    return map;
  }, [rows, isProduct]);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {photos.map((p, i) => (
          <PhotoSummaryCard
            key={p.id}
            photo={p}
            index={i}
            itemCount={rows.filter((r) => r.__photoIndex === i).length}
            onResolveSupplier={onResolveSupplier}
          />
        ))}
      </div>

      {/* Sconto generale documento (solo per scan prodotti) */}
      {isProduct && rows.length > 1 && (
        <div data-testid="global-discount-bar" className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-[#FDF6E3] border border-[#F2DEB0]">
          <Percent size={14} className="text-[#8B6518] flex-shrink-0" />
          <span className="text-xs font-medium text-[#8B6518] min-w-0 flex-1 break-words">Sconto generale documento</span>
          <Input
            type="number"
            min="0"
            max="100"
            step="0.5"
            placeholder="0"
            value={globalDiscount}
            onChange={(e) => setGlobalDiscount(e.target.value)}
            className="h-8 w-20 text-sm font-mono text-right"
            data-testid="global-discount-input"
          />
          <span className="text-xs text-[#8B6518]">%</span>
          <Button
            size="sm"
            onClick={applyGlobalDiscount}
            data-testid="global-discount-apply-btn"
            className="h-8 bg-[#8B6518] hover:bg-[#6E5113] text-white text-xs w-full sm:w-auto"
          >
            Applica a tutte
          </Button>
        </div>
      )}

      {/* Applica scadenze suggerite a tutte le righe senza scadenza */}
      {isProduct && missingExpiryCount > 0 && (
        <div data-testid="suggested-expiry-bar" className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-[#E8F3EC] border border-[#C5E1D1]">
          <CalendarCheck size={14} className="text-[#2B5A3F] flex-shrink-0" />
          <span className="text-xs font-medium text-[#2B5A3F] min-w-0 flex-1 break-words">
            🤖 {missingExpiryCount} {missingExpiryCount === 1 ? "riga senza scadenza" : "righe senza scadenza"} — applica le stime AI?
          </span>
          <Button
            size="sm"
            onClick={onApplySuggestedExpiryToAll}
            data-testid="apply-suggested-expiry-btn"
            className="h-8 bg-[#2B5A3F] hover:bg-[#1F4530] text-white text-xs w-full sm:w-auto"
          >
            Applica a tutte
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 pb-2 border-t border-[#EAE7E1]">
        <div className="text-sm text-[#5C5F5A] min-w-0 break-words">
          <strong>{rows.length}</strong> voci totali da {successPhotos.length}{" "}
          {successPhotos.length === 1 ? "foto" : "foto"}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onReset}
          className="text-xs text-[#8B6518] hover:bg-[#FDF6E3]"
          data-testid="receipt-retry-btn"
        >
          <RefreshCw size={12} className="mr-1" />
          Ricomincia
        </Button>
      </div>

      <div className="space-y-3">
        {rows.map((row, idx) => (
          <RowCard
            key={row.__id}
            index={idx}
            photoIndex={row.__photoIndex}
            row={row}
            onChange={(patch) => onUpdateRow(row.__id, patch)}
            onRemove={() => onRemoveRow(row.__id)}
            isProduct={isProduct}
            products={products}
            intraScanLink={intraScanLinks.get(row.__id) || null}
          />
        ))}
        {rows.length === 0 && (
          <div className="p-8 text-center text-sm text-[#8E918C] bg-[#F9F8F6] rounded-lg border border-dashed border-[#EAE7E1]">
            Tutte le voci sono state rimosse. Ricarica le foto.
          </div>
        )}
      </div>
    </div>
  );
}
